import { Autocomplete, Box, TextField } from '@mui/material';
import NextImage from 'next/image';
import { useMemo } from 'react';

import { sortSelectedName } from 'utils/sort.utils';

import { ALPHA2_COUNTRY_CODES } from 'constants/countries.constants';
import { useIntlDisplayNames } from 'hooks/useIntlDisplayNames';

import Icon from 'components/@icons/Icon';

import { CountryPickerProps } from './types';

const CountryPicker = ({
  priority = ['SA', 'AE'],
  label,
  value,
  onChange,
  TextFieldProps,
  inputRef,
  ...props
}: CountryPickerProps) => {
  const displayNames = useIntlDisplayNames();
  const options = useMemo(
    () =>
      ALPHA2_COUNTRY_CODES.map((code) => ({
        label: displayNames.of(code) ?? '',
        code,
      })).sort(
        sortSelectedName(priority, 'code', (a, b) =>
          a.label.localeCompare(b.label),
        ),
      ),
    [displayNames, priority],
  );

  return (
    <Autocomplete
      value={options.find(({ code }) => code === value) ?? null}
      onChange={(_event, value) => onChange(value?.code ?? null)}
      options={options}
      autoHighlight
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      renderOption={({ key, ...props }, option) => (
        <Box
          component="li"
          key={key}
          {...props}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              height: 'auto',
              mr: 2,
              flexShrink: 1,
            }}
          >
            <NextImage
              width="20"
              height="15"
              src={`https://flagcdn.com/w40/${option.code?.toLowerCase()}.webp`}
              alt={option.label}
              style={{
                objectFit: 'cover',
              }}
              unoptimized
            />
          </Box>
          {option.label} ({option.code})
        </Box>
      )}
      popupIcon={<Icon name="ChevronDown" fontSize={24} sx={{ m: 0.5 }} />}
      clearIcon={
        <Icon
          name="Close"
          fontSize={16}
          sx={{ m: 0.5, color: 'text.secondary' }}
        />
      }
      renderInput={(params) => (
        <TextField
          inputRef={inputRef}
          variant="filled"
          label={label}
          {...TextFieldProps}
          {...params}
          slotProps={{
            htmlInput: { ...params.inputProps },
          }}
        />
      )}
      {...props}
      slotProps={{
        popupIndicator: { variant: 'standard' },
        clearIndicator: { variant: 'standard' },
        paper: { sx: { mb: 2 } },
      }}
    />
  );
};

export default CountryPicker;
